import React, { useEffect, useMemo, useState } from 'react'
import Modal from '../../components/UI/Modal'
import { getApiErrorMessage, reviewPayment } from '../../api/adminPaymentsApi'

const money = (value) => `${Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`
const dateTime = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—'
const idOf = (value) => String(value?._id || value?.id || value || '')

const copyText = async (value) => {
  const text = String(value || '')
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
  } catch (_error) {
    // Clipboard API может быть запрещён браузером. Само окно остаётся рабочим.
  }
}

const DetailLine = ({ label, value, mono = false, copy = false }) => (
  <div className="admin-payments__detail-line">
    <span>{label}</span>
    <div>
      <b className={mono ? 'is-mono' : ''}>{value ?? '—'}</b>
      {copy && value ? (
        <button type="button" className="admin-payments__copy" onClick={() => copyText(value)}>Копировать</button>
      ) : null}
    </div>
  </div>
)

const entityTitle = (entity) => {
  if (!entity) return '—'
  return [entity.firstName, entity.lastName].filter(Boolean).join(' ')
    || entity.name
    || entity.email
    || entity.username
    || idOf(entity)
}

export default function PaymentDetailsModal({ show, data, loading, onClose, onChanged, navigate }) {
  const payment = data?.payment || null
  const relations = data?.relations || {}
  const [adminNote, setAdminNote] = useState('')
  const [reviewStatus, setReviewStatus] = useState('UNREVIEWED')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setAdminNote(payment?.adminNote || '')
    setReviewStatus(payment?.reviewStatus || 'UNREVIEWED')
    setError('')
  }, [payment?._id, payment?.adminNote, payment?.reviewStatus])

  const title = useMemo(() => {
    if (!payment) return 'Платёж'
    return `Платёж ${payment.providerPaymentId || payment.merchantOrderId || idOf(payment)}`
  }, [payment])

  const handleSaveReview = async () => {
    if (!payment?._id) return
    setSaving(true)
    setError('')
    try {
      const updated = await reviewPayment(payment._id, { adminNote, reviewStatus })
      onChanged?.(updated)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось сохранить проверку'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} handleClose={onClose} modalTitle={title} size="xl">
      <div className="admin-payments__detail-modal">
        {loading ? <div className="admin-payments__loading">Загрузка деталей…</div> : null}
        {!loading && data?.error ? <div className="admin-payments__error">{data.error}</div> : null}
        {!loading && !payment && !data?.error ? <div className="admin-payments__empty">Платёж не найден.</div> : null}

        {!loading && payment ? (
          <>
            <div className="admin-payments__detail-grid">
              <section>
                <h4>Банк</h4>
                <DetailLine label="Провайдер" value={payment.provider || 'TBANK'} />
                <DetailLine label="Статус системы" value={payment.status} />
                <DetailLine label="Статус банка" value={payment.providerStatus || '—'} />
                <DetailLine label="Сумма" value={money(payment.amount)} />
                <DetailLine label="PaymentId" value={payment.providerPaymentId || '—'} mono copy />
                <DetailLine label="OrderId T-Bank" value={payment.merchantOrderId || '—'} mono copy />
                <DetailLine label="Наш paymentId" value={idOf(payment)} mono copy />
                <DetailLine label="Создан" value={dateTime(payment.createdAt)} />
                <DetailLine label="Применён" value={dateTime(payment.appliedAt)} />
              </section>

              <section>
                <h4>Назначение</h4>
                <DetailLine label="Тип" value={payment.purpose} />
                <DetailLine label="Описание" value={payment.description || '—'} />
                <DetailLine label="База" value={money(payment.baseAmount)} />
                <DetailLine label="Бонус" value={money(payment.bonusAmount)} />
                <DetailLine label="Получатель" value={`${payment.targetModel || '—'} ${entityTitle(relations.target)}`} />
                <DetailLine label="Scope лицензии" value={`${payment.licenseScopeModel || '—'} ${entityTitle(relations.licenseScope)}`} />
                <DetailLine label="Промокод" value={payment.promoCode?.code || '—'} />
              </section>

              <section>
                <h4>Связанные сущности</h4>
                <DetailLine label="Пользователь" value={entityTitle(payment.initiatorId)} />
                <DetailLine label="User ID" value={idOf(payment.initiatorId)} mono copy />
                <DetailLine label="Офис/получатель ID" value={idOf(payment.targetId)} mono copy />
                <DetailLine label="Заказ" value={payment.orderId?.orderNumber || idOf(payment.orderId) || '—'} />
                <DetailLine label="Order ID" value={idOf(payment.orderId)} mono copy />
                <DetailLine label="Цифровое предложение" value={payment.digitalOfferId?.title || payment.digitalOfferId?.code || '—'} />
                <DetailLine label="Offer ID" value={idOf(payment.digitalOfferId)} mono copy />
                <div className="admin-payments__entity-actions">
                  {payment.initiatorId ? <button type="button" onClick={() => navigate('/users')}>Открыть пользователей</button> : null}
                  {payment.orderId ? <button type="button" onClick={() => navigate('/orders')}>Открыть заказы</button> : null}
                  {payment.digitalOfferId?.productId ? <button type="button" onClick={() => navigate('/admin/payments?tab=digital')}>Открыть цифровые товары</button> : null}
                </div>
              </section>

              <section>
                <h4>Ошибка / диагностика</h4>
                <DetailLine label="Код ошибки" value={payment.providerErrorCode || '—'} mono />
                <DetailLine label="Сообщение" value={payment.providerErrorMessage || '—'} />
                <DetailLine label="Проверка" value={payment.reviewStatus || 'UNREVIEWED'} />
                <DetailLine label="Проверил" value={entityTitle(payment.reviewedBy)} />
                <DetailLine label="Когда" value={dateTime(payment.reviewedAt)} />
                <p className="admin-payments__safety-note">
                  Финансовый статус платежа здесь намеренно не редактируется вручную. Его меняет только платёжный контур и подтверждённый webhook.
                </p>
              </section>
            </div>

            <section className="admin-payments__linked-block">
              <h4>Внутренние проводки</h4>
              {relations.transactions?.length ? (
                <div className="admin-payments__linked-list">
                  {relations.transactions.map((tx) => (
                    <div key={tx._id} className="admin-payments__linked-row">
                      <span>{dateTime(tx.createdAt)}</span>
                      <b>{tx.type}</b>
                      <span>{money(tx.amount)}</span>
                      <span>{tx.balanceBefore ?? '—'} → {tx.balanceAfter ?? '—'}</span>
                      <span>{tx.description || '—'}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="admin-payments__empty compact">Нет связанных проводок.</div>}
            </section>

            <section className="admin-payments__linked-block">
              <h4>Выданные лицензии</h4>
              {relations.licenses?.length ? (
                <div className="admin-payments__linked-list">
                  {relations.licenses.map((license) => (
                    <div key={license._id} className="admin-payments__linked-row">
                      <span>{dateTime(license.createdAt)}</span>
                      <b>{license.status}</b>
                      <span>{license.offerId?.title || license.offerId?.code || idOf(license.offerId)}</span>
                      <span>{license.scopeModel}: {entityTitle(license.scopeId)}</span>
                      <span>{license.paymentMethod}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="admin-payments__empty compact">Лицензий нет.</div>}
            </section>

            <section className="admin-payments__review-box">
              <div>
                <h4>Служебная проверка</h4>
                <p>Заметка не влияет на деньги и банковский статус. Она нужна для разборов FAILED / REFUND / спорных операций.</p>
              </div>
              <select value={reviewStatus} onChange={(event) => setReviewStatus(event.target.value)}>
                <option value="UNREVIEWED">Не проверено</option>
                <option value="REVIEWED">Проверено</option>
              </select>
              <textarea
                value={adminNote}
                onChange={(event) => setAdminNote(event.target.value)}
                placeholder="Что проверили, что произошло, что нужно сделать…"
                rows={4}
              />
              {error ? <div className="admin-payments__error">{error}</div> : null}
              <button type="button" className="admin-payments__primary" disabled={saving} onClick={handleSaveReview}>
                {saving ? 'Сохранение…' : 'Сохранить проверку'}
              </button>
            </section>
          </>
        ) : null}
      </div>
    </Modal>
  )
}
